import { useState, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Upload, 
  FileText, 
  Sparkles, 
  AlertCircle,
  CheckCircle2,
  Calendar,
  MapPin,
  Car,
  Plus
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useTrafficFines, OffenseType, OFFENSE_TYPE_LABELS, CreateFineInput } from '@/hooks/useTrafficFines';
import { useVehicles } from '@/hooks/useVehicles';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface FineUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ExtractedData {
  reference_number?: string | null;
  license_plate?: string | null;
  offense_date?: string | null;
  offense_location?: string | null;
  offense_type?: string;
  offense_description?: string | null;
  speed_recorded?: number | null;
  speed_limit?: number | null;
  speed_retained?: number | null;
  fine_amount?: number | null;
  reduced_amount?: number | null;
  increased_amount?: number | null;
  points_withdrawn?: number | null;
  fine_class?: number | null;
  payment_deadline?: string | null;
  reduced_deadline?: string | null;
  designation_deadline?: string | null;
  confidence_score?: number;
}

export function FineUploadDialog({ open, onOpenChange }: FineUploadDialogProps) {
  const { user } = useAuth();
  const { createFine } = useTrafficFines();
  const { vehicles, createVehicle } = useVehicles();
  const [step, setStep] = useState<'upload' | 'extracting' | 'form'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [formData, setFormData] = useState<Partial<CreateFineInput>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveDocument, setSaveDocument] = useState(true);
  const [addVehicleToFleet, setAddVehicleToFleet] = useState(false);
  const [isCreatingVehicle, setIsCreatingVehicle] = useState(false);

  // Check if the detected plate exists in the fleet
  const normalizedPlate = formData.license_plate?.replace(/[-\s]/g, '').toUpperCase() || '';
  const existingVehicle = useMemo(() => {
    return vehicles.find(v => 
      v.license_plate.replace(/[-\s]/g, '').toUpperCase() === normalizedPlate
    );
  }, [vehicles, normalizedPlate]);

  const isUnknownVehicle = normalizedPlate.length >= 5 && !existingVehicle;

  const resetDialog = useCallback(() => {
    setStep('upload');
    setFile(null);
    setFilePreview(null);
    setExtractedData(null);
    setFormData({});
    setIsSubmitting(false);
    setSaveDocument(true);
    setAddVehicleToFleet(false);
    setIsCreatingVehicle(false);
  }, []);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setFile(file);
    
    // Create preview
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }

    // Start extraction
    setStep('extracting');

    try {
      // Convert file to base64
      const arrayBuffer = await file.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );

      const { data, error } = await supabase.functions.invoke('extract-traffic-fine', {
        body: { imageBase64: base64, mimeType: file.type }
      });

      if (error) throw error;

      if (data?.success && data?.data) {
        const extracted = data.data as ExtractedData;
        setExtractedData(extracted);
        
        // Pre-fill form with extracted data
        setFormData({
          license_plate: extracted.license_plate || '',
          offense_type: (extracted.offense_type as OffenseType) || 'other',
          offense_date: extracted.offense_date || null,
          offense_location: extracted.offense_location || null,
          offense_description: extracted.offense_description || null,
          reference_number: extracted.reference_number || null,
          fine_amount: extracted.fine_amount || null,
          reduced_amount: extracted.reduced_amount || null,
          increased_amount: extracted.increased_amount || null,
          points_withdrawn: extracted.points_withdrawn || null,
          fine_class: extracted.fine_class || null,
          payment_deadline: extracted.payment_deadline || null,
          reduced_deadline: extracted.reduced_deadline || null,
          designation_deadline: extracted.designation_deadline || null,
          speed_recorded: extracted.speed_recorded || null,
          speed_limit: extracted.speed_limit || null,
          speed_retained: extracted.speed_retained || null,
          extracted_data: extracted as Record<string, unknown>,
          confidence_score: extracted.confidence_score || null,
        });
        
        setStep('form');
        toast.success('Données extraites avec succès');
      } else {
        throw new Error(data?.error || 'Extraction failed');
      }
    } catch (error) {
      console.error('Extraction error:', error);
      toast.error("Erreur lors de l'extraction. Vous pouvez remplir manuellement.");
      setStep('form');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  const handleSubmit = async () => {
    if (!formData.license_plate) {
      toast.error('La plaque d\'immatriculation est requise');
      return;
    }

    setIsSubmitting(true);

    try {
      let vehicleId: string | null = null;
      let documentId: string | null = null;

      // Create vehicle if user opted to add it
      if (addVehicleToFleet && isUnknownVehicle && formData.license_plate) {
        setIsCreatingVehicle(true);
        try {
          const newVehicle = await createVehicle.mutateAsync({
            license_plate: formData.license_plate.replace(/[-\s]/g, '').toUpperCase(),
            brand: 'À compléter',
            model: 'À compléter',
            category: 'vl',
            status: 'active',
            organization_id: null,
            first_registration_date: null,
            vin: null,
            fuel_type: null,
            acquisition_type: null,
            acquisition_date: null,
            notes: `Véhicule créé automatiquement depuis une amende`,
          });
          vehicleId = newVehicle.id;
          toast.success('Véhicule ajouté à la flotte');
        } catch (error) {
          console.error('Error creating vehicle:', error);
          // Continue without vehicle link - don't block the fine creation
        } finally {
          setIsCreatingVehicle(false);
        }
      } else if (existingVehicle) {
        vehicleId = existingVehicle.id;
      }

      // Upload and save document if file exists and user wants to save it
      if (file && saveDocument && user?.id) {
        const fileExt = file.name.split('.').pop() || 'pdf';
        const fileName = `${Date.now()}_fine.${fileExt}`;
        const filePath = `${user.id}/fines/${fileName}`;

        // Upload to storage
        const { error: uploadError } = await supabase.storage
          .from('documents')
          .upload(filePath, file);

        if (uploadError) {
          console.error('Upload error:', uploadError);
          toast.error("Erreur lors de l'upload du document");
        } else {
          // Create document record
          const { data: docData, error: docError } = await supabase
            .from('documents')
            .insert([{
              user_id: user.id,
              title: formData.reference_number 
                ? `Amende ${formData.reference_number}` 
                : `Amende ${formData.license_plate}`,
              file_name: file.name,
              file_path: filePath,
              mime_type: file.type,
              file_size: file.size,
              source: 'upload' as const,
              doc_type: 'other' as const,
              status: 'complete' as const,
            }])
            .select('id')
            .single();

          if (docError) {
            console.error('Document creation error:', docError);
          } else {
            documentId = docData.id;
          }
        }
      }

      await createFine.mutateAsync({
        ...formData,
        document_id: documentId,
        vehicle_id: vehicleId,
      } as CreateFineInput);
      
      onOpenChange(false);
      resetDialog();
    } catch (error) {
      console.error('Submit error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(resetDialog, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Ajouter une amende
          </DialogTitle>
        </DialogHeader>

        {step === 'upload' && (
          <div
            {...getRootProps()}
            className={cn(
              "border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors",
              isDragActive 
                ? "border-primary bg-primary/5" 
                : "border-muted-foreground/25 hover:border-primary/50"
            )}
          >
            <input {...getInputProps()} />
            <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">
              {isDragActive ? "Déposez le fichier ici" : "Glissez-déposez votre avis de contravention"}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              ou cliquez pour sélectionner
            </p>
            <p className="text-xs text-muted-foreground">
              PDF, JPG, PNG • Max 10 Mo
            </p>
          </div>
        )}

        {step === 'extracting' && (
          <div className="py-12 text-center">
            <Spinner className="h-12 w-12 mx-auto mb-4" />
            <p className="text-lg font-medium mb-2 flex items-center justify-center gap-2">
              <Sparkles className="h-5 w-5 text-primary animate-pulse" />
              Extraction en cours...
            </p>
            <p className="text-sm text-muted-foreground">
              L'IA analyse votre document pour extraire les informations
            </p>
          </div>
        )}

        {step === 'form' && (
          <div className="space-y-6">
            {/* Confidence indicator */}
            {extractedData?.confidence_score && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                {extractedData.confidence_score >= 80 ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                )}
                <span className="text-sm">
                  Confiance de l'extraction : {extractedData.confidence_score}%
                </span>
                <Badge variant={extractedData.confidence_score >= 80 ? "default" : "secondary"}>
                  {extractedData.confidence_score >= 80 ? "Fiable" : "À vérifier"}
                </Badge>
              </div>
            )}

            {/* Preview if available */}
            {filePreview && (
              <div className="border rounded-lg overflow-hidden max-h-48">
                <img 
                  src={filePreview} 
                  alt="Aperçu du document" 
                  className="w-full h-full object-contain bg-muted"
                />
              </div>
            )}

            {/* Form fields */}
            <div className="grid grid-cols-2 gap-4">
              {/* License plate - Required */}
              <div className="space-y-2">
                <Label htmlFor="license_plate" className="flex items-center gap-1">
                  <Car className="h-3 w-3" />
                  Plaque d'immatriculation *
                </Label>
                <Input
                  id="license_plate"
                  value={formData.license_plate || ''}
                  onChange={(e) => setFormData({ ...formData, license_plate: e.target.value.toUpperCase() })}
                  placeholder="AA-123-BB"
                  className="font-mono"
                />
              </div>

              {/* Reference number */}
              <div className="space-y-2">
                <Label htmlFor="reference_number">N° de contravention</Label>
                <Input
                  id="reference_number"
                  value={formData.reference_number || ''}
                  onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                  placeholder="1234567890123"
                />
              </div>

              {/* Vehicle not in fleet alert */}
              {isUnknownVehicle && (
                <div className="col-span-2">
                  <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
                    <Car className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    <AlertDescription className="flex items-center justify-between">
                      <span className="text-sm text-amber-800 dark:text-amber-200">
                        Ce véhicule ({formData.license_plate}) n'est pas dans votre flotte
                      </span>
                      <Button
                        type="button"
                        variant={addVehicleToFleet ? "default" : "outline"}
                        size="sm"
                        onClick={() => setAddVehicleToFleet(!addVehicleToFleet)}
                        className="ml-3 shrink-0"
                      >
                        {addVehicleToFleet ? (
                          <>
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Sera ajouté
                          </>
                        ) : (
                          <>
                            <Plus className="h-4 w-4 mr-1" />
                            Ajouter à la flotte
                          </>
                        )}
                      </Button>
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              {/* Existing vehicle indicator */}
              {existingVehicle && (
                <div className="col-span-2">
                  <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30">
                    <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <AlertDescription className="text-sm text-green-800 dark:text-green-200">
                      Véhicule reconnu : {existingVehicle.brand} {existingVehicle.model}
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              {/* Offense type */}
              <div className="space-y-2">
                <Label>Type d'infraction</Label>
                <Select
                  value={formData.offense_type || 'other'}
                  onValueChange={(value) => setFormData({ ...formData, offense_type: value as OffenseType })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(OFFENSE_TYPE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Offense date */}
              <div className="space-y-2">
                <Label htmlFor="offense_date" className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Date de l'infraction
                </Label>
                <Input
                  id="offense_date"
                  type="datetime-local"
                  value={formData.offense_date?.slice(0, 16) || ''}
                  onChange={(e) => setFormData({ ...formData, offense_date: e.target.value })}
                />
              </div>

              {/* Offense location */}
              <div className="col-span-2 space-y-2">
                <Label htmlFor="offense_location" className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  Lieu de l'infraction
                </Label>
                <Input
                  id="offense_location"
                  value={formData.offense_location || ''}
                  onChange={(e) => setFormData({ ...formData, offense_location: e.target.value })}
                  placeholder="Adresse, ville"
                />
              </div>

              {/* Speed fields (shown for speeding) */}
              {(formData.offense_type === 'speeding' || formData.speed_retained) && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="speed_limit">Vitesse autorisée (km/h)</Label>
                    <Input
                      id="speed_limit"
                      type="number"
                      value={formData.speed_limit || ''}
                      onChange={(e) => setFormData({ ...formData, speed_limit: parseInt(e.target.value) || null })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="speed_retained">Vitesse retenue (km/h)</Label>
                    <Input
                      id="speed_retained"
                      type="number"
                      value={formData.speed_retained || ''}
                      onChange={(e) => setFormData({ ...formData, speed_retained: parseInt(e.target.value) || null })}
                    />
                  </div>
                </>
              )}

              {/* Amount fields */}
              <div className="space-y-2">
                <Label htmlFor="fine_amount">Montant forfaitaire (€)</Label>
                <Input
                  id="fine_amount"
                  type="number"
                  step="0.01"
                  value={formData.fine_amount || ''}
                  onChange={(e) => setFormData({ ...formData, fine_amount: parseFloat(e.target.value) || null })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reduced_amount">Montant minoré (€)</Label>
                <Input
                  id="reduced_amount"
                  type="number"
                  step="0.01"
                  value={formData.reduced_amount || ''}
                  onChange={(e) => setFormData({ ...formData, reduced_amount: parseFloat(e.target.value) || null })}
                />
              </div>

              {/* Points */}
              <div className="space-y-2">
                <Label htmlFor="points_withdrawn">Points retirés</Label>
                <Input
                  id="points_withdrawn"
                  type="number"
                  min="0"
                  max="6"
                  value={formData.points_withdrawn || ''}
                  onChange={(e) => setFormData({ ...formData, points_withdrawn: parseInt(e.target.value) || null })}
                />
              </div>

              {/* Designation deadline */}
              <div className="space-y-2">
                <Label htmlFor="designation_deadline">Délai de désignation</Label>
                <Input
                  id="designation_deadline"
                  type="date"
                  value={formData.designation_deadline || ''}
                  onChange={(e) => setFormData({ ...formData, designation_deadline: e.target.value })}
                />
              </div>

              {/* Notes */}
              <div className="col-span-2 space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notes supplémentaires..."
                  rows={2}
                />
              </div>
            </div>

            {/* Save document option */}
            {file && (
              <div className="flex items-center space-x-2 p-3 rounded-lg bg-muted/50">
                <Checkbox
                  id="save-document"
                  checked={saveDocument}
                  onCheckedChange={(checked) => setSaveDocument(checked === true)}
                />
                <Label htmlFor="save-document" className="text-sm cursor-pointer">
                  Conserver le scan dans mes documents
                </Label>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={handleClose}>
                Annuler
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting || !formData.license_plate}>
                {isSubmitting ? <Spinner className="h-4 w-4 mr-2" /> : null}
                Enregistrer l'amende
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
